import { useEffect, useState, useMemo } from 'react';
import { Handshake, AlertTriangle, TrendingUp, Plus, PhoneCall, Trash2 } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatsCard } from '@/components/ui/StatsCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton, SkeletonStats, SkeletonTable } from '@/components/ui/Skeleton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PartnershipHealthBar } from '@/components/modules/PartnershipHealthBar';
import { EntityLink } from '@/components/ui/EntityLink';
import { formatCurrency, formatDate } from '@/lib/format';
import { PartnershipSheet } from '@/components/sheets/PartnershipSheet';
import { usePartnershipsStore } from '@/stores/partnershipsStore';
import { toast } from '@/stores/toastStore';
import type { Partnership } from '@nexus/shared/types';

const tierVariant: Record<string, 'purple' | 'info' | 'success' | 'warning'> = {
  strategic: 'purple',
  standard: 'info',
  affiliate: 'success',
  vendor: 'warning',
};

const tierOptions = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'standard', label: 'Standard' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'vendor', label: 'Vendor' },
];

export function Partnerships() {
  const { items, isLoading, error, fetch, create, remove, checkin, clearError } = usePartnershipsStore();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formTier, setFormTier] = useState('standard');
  const [formValue, setFormValue] = useState('');
  const [formCommission, setFormCommission] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const avgHealth = items.length > 0
    ? Math.round(items.reduce((sum, p) => sum + (p.healthScore ?? 0), 0) / items.length)
    : 0;
  const atRisk = items.filter((p) => (p.healthScore ?? 0) < 60).length;
  const totalValue = items.reduce((sum, p) => sum + (p.contractValue ?? 0), 0);

  const healthTrend = useMemo(() => {
    if (items.length === 0) return [];
    const avg = Math.round(items.reduce((s, p) => s + (p.healthScore || 0), 0) / items.length);
    return [{ label: 'Current', avg }];
  }, [items]);

  const handleCreate = async () => {
    if (!formName.trim()) return;
    setCreating(true);
    try {
      await create({
        name: formName.trim(),
        tier: formTier,
        contractValue: formValue ? Number(formValue) : undefined,
        commission: formCommission ? Number(formCommission) : undefined,
        notes: formNotes.trim() || undefined,
      });
      toast.success('Partnership created');
      setShowCreate(false);
      setFormName('');
      setFormTier('standard');
      setFormValue('');
      setFormCommission('');
      setFormNotes('');
    } finally {
      setCreating(false);
    }
  };

  const handleCheckin = async (id: string) => {
    try {
      await checkin(id);
      toast.success('Check-in recorded');
    } catch {
      /* handled by store */
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      await remove(confirmDelete);
      setConfirmDelete(null);
    } catch {
      /* handled by store */
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: Column<Partnership>[] = [
    {
      key: 'name',
      header: 'Partner',
      sortable: true,
      render: (row) => {
        if (row.companyId) {
          return <EntityLink type="company" id={row.companyId}><span className="text-sm font-medium">{row.name}</span></EntityLink>;
        }
        if (row.contactId) {
          return <EntityLink type="contact" id={row.contactId}><span className="text-sm font-medium">{row.name}</span></EntityLink>;
        }
        return <span className="text-sm font-medium text-surface-200">{row.name}</span>;
      },
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (row) => <Badge variant={tierVariant[row.tier] || 'default'}>{row.tier}</Badge>,
    },
    {
      key: 'health',
      header: 'Health Score',
      sortable: true,
      render: (row) => <PartnershipHealthBar score={row.healthScore ?? 0} />,
    },
    {
      key: 'value',
      header: 'Contract Value',
      sortable: true,
      render: (row) => (
        <span className="text-sm font-medium text-surface-100">
          {row.contractValue != null ? formatCurrency(row.contractValue) : '--'}
        </span>
      ),
    },
    {
      key: 'nextCheckin',
      header: 'Next Check-in',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-surface-400">
          {row.nextCheckinAt ? formatDate(row.nextCheckinAt) : '--'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge variant={row.status === 'active' ? 'success' : 'default'}>{row.status}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.status === 'active' && (
            <Button
              variant="ghost"
              size="sm"
              icon={<PhoneCall className="h-3.5 w-3.5" />}
              onClick={() => handleCheckin(row.id)}
            >
              Check-in
            </Button>
          )}
          <button
            onClick={() => setConfirmDelete(row.id)}
            className="p-1.5 rounded-lg text-surface-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Delete partnership"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading && items.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-32 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-9 w-36 rounded-lg" />
        </div>
        {/* Stats — 4 cards */}
        <SkeletonStats count={4} />
        {/* Main grid: table 2/3 + chart 1/3 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonTable rows={5} cols={7} />
          </div>
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <Skeleton className="h-4 w-28 mb-4" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Partnerships</h2>
          <p className="text-sm text-surface-400 mt-0.5">Partner relationship management and health monitoring</p>
        </div>
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
          Add Partnership
        </Button>
      </div>

      {error && (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard label="Total Partners" value={items.length} icon={<Handshake className="h-5 w-5" />} />
        <StatsCard label="Avg Health Score" value={avgHealth} icon={<TrendingUp className="h-5 w-5" />} />
        <StatsCard label="At Risk" value={atRisk} icon={<AlertTriangle className="h-5 w-5" />} />
        <StatsCard label="Total Contract Value" value={formatCurrency(totalValue)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Partner table */}
        <div className="lg:col-span-2">
          <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
            <DataTable columns={columns} data={items} onRowClick={(row) => setSelectedId(row.id)} />
          </div>
        </div>

        {/* Health trend */}
        <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-surface-200 mb-4">Health Trend</h3>
          {healthTrend.length <= 1 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <p className="text-3xl font-bold text-surface-100">{healthTrend[0]?.avg ?? '--'}</p>
              <p className="text-sm text-surface-500 mt-1">Avg Health Score</p>
              <p className="text-xs text-surface-600 mt-3">Historical trend data not yet available</p>
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={healthTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#f1f5f9',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete Partnership"
        description="This action cannot be undone. The partnership record and associated data will be permanently removed."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteLoading}
      />

      {/* Create Partnership Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add Partnership">
        <div className="space-y-4">
          <Input
            label="Partner Name"
            placeholder="Enter partner name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
          />
          <Select
            label="Tier"
            options={tierOptions}
            value={formTier}
            onChange={(e) => setFormTier(e.target.value)}
          />
          <Input
            label="Contract Value"
            type="number"
            placeholder="0"
            value={formValue}
            onChange={(e) => setFormValue(e.target.value)}
          />
          <Input
            label="Commission (%)"
            type="number"
            placeholder="0"
            min={0}
            max={100}
            value={formCommission}
            onChange={(e) => setFormCommission(e.target.value)}
          />
          <div className="w-full">
            <label className="block text-sm font-medium text-surface-300 mb-1.5">Notes</label>
            <textarea
              placeholder="Optional notes"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent hover:border-surface-600 transition-colors resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button loading={creating} onClick={handleCreate} disabled={!formName.trim()}>Create</Button>
          </div>
        </div>
      </Modal>

      {/* Partnership Detail Sheet */}
      <PartnershipSheet
        id={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
